/**
 * Timezone aware opening hours.
 *
 * The server runs in UTC, the tablet may sit in any timezone and the tenant
 * configures wall-clock times. All comparisons therefore happen on the local
 * calendar/clock fields derived through Intl, never on raw UTC offsets - that
 * keeps DST transitions correct without pulling in a date library.
 */
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const WEEKDAY_INDEX = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
};
/** Formatter cache - constructing Intl formatters is comparatively expensive. */
const formatterCache = new Map();
function formatterFor(timezone) {
    let f = formatterCache.get(timezone);
    if (!f) {
        f = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            weekday: 'short',
        });
        formatterCache.set(timezone, f);
    }
    return f;
}
export function isValidTimezone(timezone) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: timezone });
        return true;
    }
    catch {
        return false;
    }
}
export function toLocalMoment(at, timezone) {
    const tz = isValidTimezone(timezone) ? timezone : 'UTC';
    const parts = formatterFor(tz).formatToParts(at);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    const hour = get('hour') === '24' ? '00' : get('hour');
    return {
        date: `${get('year')}-${get('month')}-${get('day')}`,
        minutes: Number(hour) * 60 + Number(get('minute')),
        weekday: WEEKDAY_INDEX[get('weekday')] ?? 1,
    };
}
export function parseTime(value) {
    const m = TIME_RE.exec(value);
    if (!m)
        return null;
    return Number(m[1]) * 60 + Number(m[2]);
}
export function isValidInterval(interval) {
    const from = parseTime(interval.from);
    const to = parseTime(interval.to);
    return from !== null && to !== null && to > from;
}
/**
 * Resolves the intervals that apply on a given local date, honouring
 * exceptions (win over everything) and holidays (close the whole day).
 */
export function intervalsForDate(hours, local) {
    const exception = hours.exceptions?.[local.date];
    if (exception)
        return { intervals: exception.filter(isValidInterval), reason: 'exception' };
    if (hours.holidays?.includes(local.date))
        return { intervals: [], reason: 'holiday' };
    const day = hours.days?.[String(local.weekday)] ?? [];
    return { intervals: day.filter(isValidInterval), reason: 'weekday' };
}
export function getOpeningStatus(hours, at = new Date()) {
    const local = toLocalMoment(at, hours.timezone);
    const { intervals, reason } = intervalsForDate(hours, local);
    const sorted = [...intervals].sort((a, b) => (parseTime(a.from) ?? 0) - (parseTime(b.from) ?? 0));
    const current = sorted.find((i) => {
        const from = parseTime(i.from);
        const to = parseTime(i.to);
        return local.minutes >= from && local.minutes < to;
    }) ?? null;
    if (current) {
        return {
            open: true,
            current,
            nextToday: null,
            reason: 'open',
            localDate: local.date,
            localWeekday: local.weekday,
        };
    }
    const nextToday = sorted.find((i) => (parseTime(i.from) ?? 0) > local.minutes) ?? null;
    let closedReason = 'closed_time';
    if (sorted.length === 0)
        closedReason = reason === 'holiday' ? 'closed_holiday' : 'closed_day';
    return {
        open: false,
        current: null,
        nextToday,
        reason: closedReason,
        localDate: local.date,
        localWeekday: local.weekday,
    };
}
export function isOpen(hours, at = new Date()) {
    return getOpeningStatus(hours, at).open;
}
/** Validates a configured schedule before it is persisted. */
export function validateOpeningHours(hours) {
    const issues = [];
    if (!isValidTimezone(hours.timezone)) {
        issues.push({ path: 'timezone', message: `"${hours.timezone}" is not a valid IANA timezone` });
    }
    const checkDay = (key, list, base) => {
        const sorted = [...list].sort((a, b) => (parseTime(a.from) ?? 0) - (parseTime(b.from) ?? 0));
        sorted.forEach((interval, i) => {
            const p = `${base}.${key}[${i}]`;
            if (parseTime(interval.from) === null) {
                issues.push({ path: `${p}.from`, message: 'from must be HH:MM' });
            }
            if (parseTime(interval.to) === null) {
                issues.push({ path: `${p}.to`, message: 'to must be HH:MM' });
            }
            if (parseTime(interval.from) !== null &&
                parseTime(interval.to) !== null &&
                !isValidInterval(interval)) {
                issues.push({ path: p, message: 'to must be after from (overnight hours are not supported)' });
            }
            const prev = sorted[i - 1];
            if (prev && parseTime(interval.from) < parseTime(prev.to)) {
                issues.push({ path: p, message: 'intervals must not overlap' });
            }
        });
    };
    for (const [key, list] of Object.entries(hours.days ?? {})) {
        if (!/^[1-7]$/.test(key)) {
            issues.push({ path: `days.${key}`, message: 'day key must be 1 (Monday) to 7 (Sunday)' });
            continue;
        }
        checkDay(key, list ?? [], 'days');
    }
    for (const date of hours.holidays ?? []) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            issues.push({ path: `holidays`, message: `"${date}" must be YYYY-MM-DD` });
        }
    }
    for (const [date, list] of Object.entries(hours.exceptions ?? {})) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            issues.push({ path: `exceptions.${date}`, message: 'exception key must be YYYY-MM-DD' });
            continue;
        }
        checkDay(date, list ?? [], 'exceptions');
    }
    return issues;
}
/**
 * German nationwide public holidays for a year, computed from Easter.
 *
 * Only the nationwide set is generated - state specific holidays differ per
 * Bundesland and are maintained by the tenant in the admin UI. Shipping a wrong
 * "Fronleichnam" for Siegen would be worse than shipping none.
 */
export function germanNationalHolidays(year) {
    const easter = easterSunday(year);
    const iso = (d) => d.toISOString().slice(0, 10);
    const shift = (days) => {
        const d = new Date(easter.getTime());
        d.setUTCDate(d.getUTCDate() + days);
        return iso(d);
    };
    return [
        `${year}-01-01`, // Neujahr
        shift(-2), // Karfreitag
        shift(1), // Ostermontag
        `${year}-05-01`, // Tag der Arbeit
        shift(39), // Christi Himmelfahrt
        shift(50), // Pfingstmontag
        `${year}-10-03`, // Tag der Deutschen Einheit
        `${year}-12-25`,
        `${year}-12-26`,
    ].sort();
}
/** Anonymous Gregorian computus. */
function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Date.UTC(year, month - 1, day));
}
export function defaultOpeningHours(timezone = 'Europe/Berlin') {
    const office = [
        { from: '08:00', to: '12:30' },
        { from: '13:30', to: '17:00' },
    ];
    return {
        timezone,
        days: {
            '1': office,
            '2': office,
            '3': office,
            '4': office,
            '5': [{ from: '08:00', to: '13:00' }],
            '6': [],
            '7': [],
        },
        holidays: germanNationalHolidays(new Date().getUTCFullYear()),
        exceptions: {},
    };
}
//# sourceMappingURL=opening-hours.js.map